import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Input, Label, MultiSelect, RadioGroup, SectionTitle } from './FormComponents';
import {
  ALL_LOCATION_OPTIONS,
  LOCATION_OPTIONS_FL,
  LOCATION_OPTIONS_NY,
  ROLE_OPTIONS,
  SHIFT_OPTIONS,
} from '../../lib/availabilityOptions';

import type { Availability } from '../../types/application';

export type StepDErrors = Partial<Record<keyof Availability, string>>;

interface StepDAvailabilityProps {
  value: Availability;
  errors: StepDErrors;
  onChange: (patch: Partial<Availability>) => void;
  applicantState?: string;
}

export function StepDAvailability({ value, errors, onChange, applicantState }: StepDAvailabilityProps) {
  const { t } = useTranslation();

  const locationOptions = useMemo(() => {
    if (applicantState === 'FL') return LOCATION_OPTIONS_FL;
    if (applicantState === 'NY') return LOCATION_OPTIONS_NY;
    return ALL_LOCATION_OPTIONS;
  }, [applicantState]);

  return (
    <div className='space-y-6'>
      <SectionTitle title={t('wizard.stepD.title')} subtitle={t('wizard.stepD.subtitle')} />

      {/* Desired Roles */}
      <div>
        <Label required error={errors.roles}>
          {t('wizard.stepD.desiredRoles')}
        </Label>
        <p className='text-xs text-gray-500 mb-2'>{t('wizard.stepD.selectMultiple')}</p>
        <MultiSelect
          options={ROLE_OPTIONS}
          selected={value.roles}
          onChange={(roles) => onChange({ roles })}
          error={errors.roles}
          columns={3}
        />
      </div>

      {/* Location Preferences */}
      <div>
        <Label required error={errors.locations}>
          {t('wizard.stepD.locationPreferences')}
        </Label>
        <p className='text-xs text-gray-500 mb-2'>{t('wizard.stepD.selectMultiple')}</p>
        <MultiSelect
          options={locationOptions}
          selected={value.locations}
          onChange={(locations) => onChange({ locations })}
          error={errors.locations}
          columns={3}
        />
      </div>

      {/* Shift Preferences */}
      <div>
        <Label required error={errors.shifts}>
          {t('wizard.stepD.shiftPreferences')}
        </Label>
        <p className='text-xs text-gray-500 mb-2'>{t('wizard.stepD.selectMultiple')}</p>
        <MultiSelect
          options={SHIFT_OPTIONS}
          selected={value.shifts}
          onChange={(shifts) => onChange({ shifts })}
          error={errors.shifts}
          columns={3}
        />
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {/* Reliable Transportation */}
        <div>
          <Label required>{t('wizard.stepD.reliableTransport')}</Label>
          <RadioGroup
            name='hasTransport'
            value={value.hasTransport}
            options={[
              { value: true, label: t('common.yes') },
              { value: false, label: t('common.no') },
            ]}
            onChange={(val) => onChange({ hasTransport: val as boolean })}
            inline
          />
        </div>

        {/* Earliest Start Date */}
        <div>
          <Label required error={errors.startDate}>
            {t('wizard.stepD.earliestStartDate')}
          </Label>
          <Input
            type='date'
            value={value.startDate}
            onChange={(e) => onChange({ startDate: e.target.value })}
            min={new Date().toISOString().split('T')[0]}
            error={errors.startDate}
          />
        </div>
      </div>
    </div>
  );
}
